import { useState, useEffect, useRef } from 'react';
import { client, getBaseUrl } from 'redux/actions/sendData';
import { get, set, unset } from 'global/lib/lodash';
import { useStore, useDispatch, useSelector } from 'react-redux';
import qs from 'qs';
import normalize from 'json-api-normalizer';
import build from 'redux-object';
import { guid, parseJson } from 'global/utils';
import { sendUserToLogout } from 'redux/actions/userLogout';
import { isDev, timeGreaterThen, _size, wait, cleanCopy } from '../utils';
import { SET_DATA } from 'redux/actions/types';

const tDefaults = {
  useNamespace: true,
  params: {},
  build: [],
  transformData: (d) => d,
  useEffect: false,
  normalize: true,
};

const defaults = {
  delay: 2000,
  tries: 0,
  maxTries: 3,
  simulate: false,
};

let ALERTOPEN = false;

const ACTIVEREQUESTS = {};

const useRetrieveData = (req, _tOpts = {}, effects = []) => {
  const [meta, setMeta] = useState({});
  const [error, setError] = useState();
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isMounted, setIsMounted] = useState(true);
  const currentOpts = useRef();
  const currentUrl = useRef();
  const currentKeys = useRef();
  const store = useStore();
  const dispatch = useDispatch();
  const lang = useSelector((state) => get(state, 'view.lang.id'));

  const tOpts = {
    ...tDefaults,
    ..._tOpts,
  };

  const tOptsCheck = (tOpts.useEffect || tOpts.waitFor === null) ? _tOpts : false;
  const showAlert = get(tOpts, 'showAlert', true);

  const buildURL = () => {
    let url = getBaseUrl(req.url, tOpts);

    if (tOpts.useLangFilter) {
      set(tOpts, 'params.filter.lang', lang);
    }
    if (Object.keys(tOpts.params).length > 0) {
      url += `?${qs.stringify(tOpts.params)}`;
    }
    //TODO make this more future proof
    if (tOpts.version) {
      url = url.replace('/v1/', `/${tOpts.version}/`);
    }
    return url;
  };

  const builtUrl = buildURL();

  const storekey = `data.${builtUrl.split('/').join('').split('.').join('')}`;

  const useCache = get(tOpts, 'useCache', false);
  const [data, _setData] = useState(useCache ? cleanCopy(get(store.getState(), `${storekey}.data`, [])) : []);

  const clearActive = () => {
    if (useCache) {
      unset(ACTIVEREQUESTS, builtUrl);
    }
  }

  const setData = (d, original = null) => {
    _setData(d);
    const dtouse = original || d;
    if ((tOpts.store || tOpts.useCache) && _size(dtouse)) {
      const sState = { ...store.getState() };

      const currentDate = new Date().toISOString();

      const explicitstore = get(tOpts, 'useCache.store');
      if (!!explicitstore) {
        set(sState, explicitstore, dtouse);
      }

      set(sState, storekey, {
        data: dtouse,
        updatedAt: get(currentKeys, 'current.usedCache') ? get(sState, `${storekey}.updatedAt`, currentDate) : currentDate,
      });

      dispatch({
        type: SET_DATA,
        data: { ...get(sState, 'data') },
      });

      clearActive();
    }
  }

  const sendRequest = async (_opts = {}) => {
    const opts = {
      ...defaults,
      ..._opts,
      ...parseJson(get(_opts, 'currentOpts', `{}`)),
    };

    if ((loading && !opts.retry) || !isMounted) return;

    try {
      setLoading(opts.loading !== undefined ? opts.loading : true);
      setError();
      const sState = store.getState();

      const headers = {
        Authorization: 'Bearer ' + get(sState, 'auth.accessToken'),
        'Content-Type': 'application/vnd.api+json',
        ...get(req, 'headers', {}),
      };

      set(currentKeys, 'current.usedCache', false);

      let resp;
      const keyObj = get(sState, storekey);
      const keydata = get(keyObj, `data`);
      const timeCheck = get(tOpts, 'useCache.reload', -1);

      const shouldReload = () => {
        const lastUpdated = get(keyObj, 'updatedAt');
        if (typeof timeCheck === 'function') {
          return timeCheck(get(keyObj, 'updatedAt'));
        } else {
          return timeGreaterThen(lastUpdated, timeCheck);
        }
      };

      if (!!keydata && !!_size(keydata) && !shouldReload()) {
        set(currentKeys, 'current.usedCache', true);
        const dta = get(tOpts, 'transformData', (dd) => dd)(keydata);
        _setData(dta);
        setLoading(false);
        return dta;
      } else if (useCache && get(ACTIVEREQUESTS, builtUrl)) {
        await wait();
        return await sendRequest(_opts);
      } {
        if (useCache) { ACTIVEREQUESTS[builtUrl] = true }
        resp = await client.request({
          method: 'get',
          ...req,
          url: builtUrl,
          headers,
        });
      }

      if (currentOpts.current !== opts.currentOpts) {
        clearActive();
        return;
      }

      if (tOpts.normalize) {
        const respData = get(resp, 'data.data', []);
        const raw = get(resp, 'data');
        const isArr = Array.isArray(respData);
        const order = isArr ? respData.map((d) => d.id) : [];
        const d = !resp.data || tOpts.returnRaw ? resp.data : normalize(resp.data);

        if (!d) {
          setData(d);
          setMeta(get(resp, 'data.meta', meta));
          setLoading(false);
          return;
        }

        const built = tOpts.returnRaw ? raw : build(d, get(tOpts, 'build.0'), get(tOpts, 'build.1'), { ignoreLinks: true });

        let orderedArray = isArr ? [] : built;
        !tOpts.returnRaw &&
          isArr &&
          (built || []).forEach((b) => {
            const index = order.indexOf(b.id);
            orderedArray[index] = b;
          });

        orderedArray = tOpts.returnRaw ? raw : (orderedArray || []).filter((a) => a);

        const transform = get(tOpts, 'transformData');
        const _built = transform(orderedArray);
        setData(_built, orderedArray);
        setMeta(get(resp, 'data.meta', meta));
        setLoading(false);
        return _built;
      } else {
        setData(resp.data);
        setMeta(get(resp, 'data.meta', meta));
        setLoading(false);
        return resp.data;
      }
    } catch (err) {
      const { status, data } = get(err, 'response', {});
      if (useCache) { unset(ACTIVEREQUESTS, builtUrl) }
      if (status >= 400 && tOpts.logoutOnUnauth) {
        if (ALERTOPEN) {
          return;
        }
        ALERTOPEN = true;
        alert('You have been logged out due to inactivity or system updates. Please login again.');
        return sendUserToLogout();
      }

      if (opts.tries < opts.maxTries) {
        await sendRequest({
          ...opts,
          tries: opts.tries + 1,
          retry: true,
        });
      } else {
        const message = getMessage(data, err);
        setError(message);
        if (isDev() && showAlert) {
          alert(message);
        }
        setLoading(false);
        throw err;
      }
    }
  };

  const getMessage = (d, e) => {
    const hasErrResp = get(d, 'message');
    if (hasErrResp) {
      const { message, exception, file } = d;
      return `${message}: ${exception}\nFile: ${file}`;
    } else {
      return e.message;
    }
  };

  const update = async (ots = {}) => {
    const newOpts = JSON.stringify({
      ...parseJson(currentOpts.current),
      token: guid(),
    });
    currentOpts.current = newOpts;
    return sendRequest({
      retry: true,
      currentOpts: newOpts,
      ...ots,
    });
  };

  const clearData = () => {
    setData([]);
  };

  const manuallySetData = (d, opts = {}) => {
    setData(d);
    setLoading(false);
  }

  useEffect(() => {
    if (tOpts.waitFor !== undefined) {
      if (!tOpts.waitFor) {
        if (tOpts.setLoadingEvenIfWait) {
          setLoading(false);
        }
        return;
      }
    }
    if (initialLoad) {
      setInitialLoad(false);
    }

    let _currentOpts = '{}';
    try {
      _currentOpts = JSON.stringify({ ...tOpts, effects });
    } catch (err) {
      console.error({ err, tOpts, effects })
      tOpts.onError && tOpts.onError(err);
    }
    if (_currentOpts !== currentOpts.current || req.url !== currentUrl.current) {
      sendRequest({ retry: true, currentOpts: _currentOpts });
    } 
    currentOpts.current = _currentOpts;
    currentUrl.current = req.url;
  }, [req.url, tOptsCheck, ...effects]);

  // useEffect(() => {
  //   return () => {
  //     // setIsMounted(false);
  //   };
  // }, []);

  useEffect(() => {
    if (tOpts.useLangFilter && get(tOpts, 'params.filter.lang', '') !== lang) {
      update();
    }
  }, [lang]);

  return {
    data,
    loading,
    setLoading,
    update,
    error,
    meta,
    clearData,
    manuallySetData,
  };
};

export default useRetrieveData;
